import type { RoutableTab } from "@/components/v2/layouts/routable-tabs-layout";
import { PLATFORM_SECTION_ENUM } from "@/utils/enums";
import { COMPANY_TIME_ATTENDANCE_PATHS } from "@/routes/company/routes.time-attendance";
import { COMPANY_DIGITAL_ID_PATHS } from "@/routes/company/routes.digital-id";
import { MS_TIME_ATTENDANCE_PATHS } from "@/routes/my-space/routes.time-attendance";
import { COMPANY_LEAVES_MANAGEMENT_PATHS } from "@/routes/company/routes.leave-management";
import { MS_LEAVES_MANAGEMENT_PATHS } from "@/routes/my-space/routes.leave-management";
import { COMPANY_NFC_PATHS } from "@/routes/company/routes.nfc";
import { COMPANY_ASSETS_PATHS } from "@/routes/company/routes.asset";
import { MS_ASSETS_PATHS } from "@/routes/my-space/routes.asset";
import { COMPANY_DOCUMENTS_PATHS } from "@/routes/company/routes.documents";
import { MS_DOCUMENTS_PATHS } from "@/routes/my-space/routes.documents";
import { COMPANY_EXPENSES_PATHS } from "@/routes/company/routes.expenses";
import { MS_EXPENSES_PATHS } from "@/routes/my-space/routes.expenses";
import { COMPANY_SALES_PATHS } from "@/routes/company/routes.sales-commissions";
import { MS_SALES_PATHS } from "@/routes/my-space/routes.sales-commissions";
import { COMPANY_OFFBOARDING_PATHS } from "@/routes/company/routes.offboarding";
import { COMPANY_ONBOARDING_PATHS } from "@/routes/company/routes.onboarding";
import { COMPANY_RECRUITMENT_PATHS } from "@/routes/company/routes.recruitment";
import { COMPANY_TEAMS_PATHS } from "@/routes/company/routes.teams";
import { MS_TEAMS_PATHS } from "@/routes/my-space/routes.teams";
import { COMPANY_SUBSCRIPTION_PATHS } from "@/routes/company/routes.subscription";
import { COMPANY_PAYROLLS_PATHS } from "@/routes/company/routes.payroll";
import { COMPANY_WORKFLOWS_PATHS } from "@/routes/company/routes.workflow";
import { COMPANY_MY_APP_PATHS } from "@/routes/company/routes.my-app";
import { MS_MY_APP_PATHS } from "@/routes/my-space/routes.my-app";
import { COMPANY_EMPLOYEES_PATHS } from "@/routes/company/routes.employees";
import { COMPANY_PROJECTS_PATHS } from "@/routes/company/routes.projects";
import { COMPANY_EVENT_PATHS } from "@/routes/company/routes.events";
import { COMPANY_ANNOUNCEMENTS_PATHS } from "@/routes/company/routes.announcement";
import { COMPANY_DATA_TRANSFER_PATHS } from "@/routes/company/routes.data-transfer";
import { COMPANY_FILES_PATHS } from "@/routes/company/routes.files";
import { MS_PERFORMANCE_PATHS } from "@/routes/my-space/routes.performance";
import { COMMON_PATHS } from "@/routes/common/routes.common";
import { MS_COMPANY_POLICIES_PATHS } from "@/routes/my-space/routes.company-policies";
import { COMPANY_PERFORMANCE_PATHS } from "@/routes/company/routes.performance";
import { MS_EVENT_PATHS } from "@/routes/my-space/routes.events";
import { COMPANY_AUDIT_LOGS_PATHS } from "@/routes/company/routes.audit-logs";
import { COMPANY_CAMERA_PATHS } from "@/routes/company/routes.camera";
import { COMPANY_BIOMETRIC_DEVICES_PATHS } from "@/routes/company/routes.biometric-devices";

/**
 * Hardcoded (frontend-only) map of a module's route → its page-level tabs.
 *
 * Single source of truth consumed by BOTH:
 *   1. the sidebar's nested accordion — each tab becomes a child link, and
 *   2. the module page's `RoutableTabsLayout` tab bar.
 *
 * Keyed by the module ROOT path, which equals the sidebar link URL
 * (`/${item.key}`) — so the sidebar can look a nav item's tabs up directly.
 *
 * NOTE: the sidebar permissions API does not (yet) return nested links, so the
 * nesting lives here on the frontend. When the API grows a children field, this
 * map can be merged with / replaced by the API response.
 */
export type NavTab = RoutableTab;

export const MODULE_NAV_TABS: Record<string, NavTab[]> = {
  // Company · Time & Attendance
  [COMPANY_TIME_ATTENDANCE_PATHS.ROOT]: [
    { label: "Overview", to: COMPANY_TIME_ATTENDANCE_PATHS.OVERVIEW },
    {
      label: "Regularize Requests",
      to: COMPANY_TIME_ATTENDANCE_PATHS.REGULARIZATION,
    },
    { label: "Overtime", to: COMPANY_TIME_ATTENDANCE_PATHS.OVERTIME },
    {
      label: "Attendance Exceptions",
      to: COMPANY_TIME_ATTENDANCE_PATHS.ATTENDANCE_EXCEPTIONS,
    },
  ],

  // My Space · Time & Attendance
  [MS_TIME_ATTENDANCE_PATHS.ROOT]: [
    {
      label: "Attendance Summary",
      to: MS_TIME_ATTENDANCE_PATHS.ATTENDANCE_SUMMARY,
    },
    { label: "Regularization", to: MS_TIME_ATTENDANCE_PATHS.REGULARIZATION },
    { label: "Overtime", to: MS_TIME_ATTENDANCE_PATHS.OVERTIME },
    {
      label: "Attendance Exceptions",
      to: MS_TIME_ATTENDANCE_PATHS.ATTENDANCE_EXCEPTIONS,
    },
  ],

  // Company · Leave Management
  [COMPANY_LEAVES_MANAGEMENT_PATHS.ROOT]: [
    { label: "All Leaves", to: COMPANY_LEAVES_MANAGEMENT_PATHS.ALL_LEAVES },
  ],

  // My Space · Leave Management (single tab lives at the module root)
  [MS_LEAVES_MANAGEMENT_PATHS.ROOT]: [
    { label: "My Leaves", to: MS_LEAVES_MANAGEMENT_PATHS.ROOT, end: true },
  ],

  // Company · NFC
  [COMPANY_NFC_PATHS.ROOT]: [
    { label: "Assigned Chips", to: COMPANY_NFC_PATHS.CHIPS },
    { label: "Forms", to: COMPANY_NFC_PATHS.FORMS },
    { label: "Submissions", to: COMPANY_NFC_PATHS.SUBMISSIONS },
    { label: "Analytics", to: COMPANY_NFC_PATHS.ANALYTICS },
  ],

  // Company · Assets
  [COMPANY_ASSETS_PATHS.ROOT]: [
    { label: "Regular Assets", to: COMPANY_ASSETS_PATHS.REGULAR_ASSETS },
    { label: "Consumable Assets", to: COMPANY_ASSETS_PATHS.CONSUMABLE_ASSETS },
    { label: "Asset Requests", to: COMPANY_ASSETS_PATHS.ASSET_REQUESTS },
  ],

  // Company · Digital ID
  [COMPANY_DIGITAL_ID_PATHS.ROOT]: [
    { label: "Employees", to: COMPANY_DIGITAL_ID_PATHS.EMPLOYEES },
  ],

  // My Space · Assets
  [MS_ASSETS_PATHS.ROOT]: [
    { label: "Assigned Assets", to: MS_ASSETS_PATHS.ASSIGNED_ASSETS },
    { label: "Requested Assets", to: MS_ASSETS_PATHS.REQUESTED_ASSETS },
  ],

  // Company · Documents
  [COMPANY_DOCUMENTS_PATHS.ROOT]: [
    { label: "Employee Documents", to: COMPANY_DOCUMENTS_PATHS.EMPLOYEE_DOCUMENTS },
    { label: "Company Documents", to: COMPANY_DOCUMENTS_PATHS.COMPANY_DOCUMENTS },
    { label: "Sent Requests", to: COMPANY_DOCUMENTS_PATHS.SENT_REQUESTS },
    { label: "Received Requests", to: COMPANY_DOCUMENTS_PATHS.RECEIVED_REQUESTS },
  ],

  // My Space · Documents
  [MS_DOCUMENTS_PATHS.ROOT]: [
    { label: "My Documents", to: MS_DOCUMENTS_PATHS.MY_DOCUMENTS },
    { label: "Sent Requests", to: MS_DOCUMENTS_PATHS.SENT_REQUESTS },
    { label: "Received Requests", to: MS_DOCUMENTS_PATHS.RECEIVED_REQUESTS },
    { label: "Signed Documents", to: MS_DOCUMENTS_PATHS.SIGNED_DOCUMENTS },
  ],

  // Company · Expenses
  [COMPANY_EXPENSES_PATHS.ROOT]: [
    { label: "All Expenses", to: COMPANY_EXPENSES_PATHS.ALL },
    { label: "Project Expenses", to: COMPANY_EXPENSES_PATHS.PROJECT },
    { label: "Asset Expenses", to: COMPANY_EXPENSES_PATHS.ASSET },
    { label: "Misc Expenses", to: COMPANY_EXPENSES_PATHS.MISC },
    { label: "Reimbursements", to: COMPANY_EXPENSES_PATHS.REIMBURSEMENTS },
    { label: "Expense Requests", to: COMPANY_EXPENSES_PATHS.REQUESTS },
  ],

  // My Space · Expenses
  [MS_EXPENSES_PATHS.ROOT]: [
    { label: "My Expenses", to: MS_EXPENSES_PATHS.MY_EXPENSES },
    { label: "My Reimbursements", to: MS_EXPENSES_PATHS.MY_REIMBURSEMENTS },
  ],

  // Company · Sales & Commissions
  [COMPANY_SALES_PATHS.ROOT]: [
    { label: "All Sales", to: COMPANY_SALES_PATHS.ALL },
    { label: "Sale Approvals", to: COMPANY_SALES_PATHS.REQUESTS },
    { label: "Commissions", to: COMPANY_SALES_PATHS.COMMISSIONS },
  ],

  // My Space · Sales & Commissions
  [MS_SALES_PATHS.ROOT]: [
    { label: "My Sales", to: MS_SALES_PATHS.MY_SALES },
    { label: "My Commission", to: MS_SALES_PATHS.MY_COMMISSION },
  ],

  // Company · Biometric Devices
  [COMPANY_BIOMETRIC_DEVICES_PATHS.ROOT]: [
    { label: "Devices", to: COMPANY_BIOMETRIC_DEVICES_PATHS.DEVICES },
    {
      label: "Unmapped Events",
      to: COMPANY_BIOMETRIC_DEVICES_PATHS.UNMAPPED_EVENTS,
    },
  ],

  // Company · Offboarding
  [COMPANY_OFFBOARDING_PATHS.ROOT]: [
    { label: "All Offboardings", to: COMPANY_OFFBOARDING_PATHS.OFFBOARDINGS },
    { label: "Pending Approvals", to: COMPANY_OFFBOARDING_PATHS.PENDING_APPROVALS },
    { label: "Final Settlements", to: COMPANY_OFFBOARDING_PATHS.FINAL_SETTLEMENTS },
  ],

  // Company · Onboarding. These 3 map to the root page's TabPaginationContainer
  // (query-param hTab, not separate routes — see COMPANY_ONBOARDING_PATHS).
  // The Start Onboarding wizard and the employee detail drill-down are
  // excluded, mirroring how Offboarding's own "start" flow stays out of its
  // tab set.
  [COMPANY_ONBOARDING_PATHS.ROOT]: [
    { label: "In Onboarding", to: COMPANY_ONBOARDING_PATHS.TAB_ONBOARDING },
    { label: "Instant Onboarded", to: COMPANY_ONBOARDING_PATHS.TAB_INSTANT },
    {
      label: "Awaiting Login",
      to: COMPANY_ONBOARDING_PATHS.TAB_AWAITING_LOGIN,
    },
  ],

  // Company · AI Recruitment
  [COMPANY_RECRUITMENT_PATHS.ROOT]: [
    { label: "Dashboard", to: COMPANY_RECRUITMENT_PATHS.TAB_DASHBOARD },
    { label: "Job Openings", to: COMPANY_RECRUITMENT_PATHS.TAB_JOBS },
    { label: "Candidates", to: COMPANY_RECRUITMENT_PATHS.TAB_CANDIDATES },
    { label: "Interviews", to: COMPANY_RECRUITMENT_PATHS.TAB_INTERVIEWS },
    { label: "Assessments", to: COMPANY_RECRUITMENT_PATHS.TAB_ASSESSMENTS },
    { label: "Offers", to: COMPANY_RECRUITMENT_PATHS.TAB_OFFERS },
    { label: "Talent Pool", to: COMPANY_RECRUITMENT_PATHS.TAB_TALENT_POOL },
  ],

  // Company · Teams (single tab lives at the module root)
  [COMPANY_TEAMS_PATHS.ROOT]: [
    { label: "Teams", to: COMPANY_TEAMS_PATHS.ROOT, end: true },
  ],

  // My Space · Teams (single tab lives at the module root)
  [MS_TEAMS_PATHS.ROOT]: [
    { label: "My Teams", to: MS_TEAMS_PATHS.ROOT, end: true },
  ],

  // Company · Payroll
  [COMPANY_PAYROLLS_PATHS.ROOT]: [
    { label: "Payrolls", to: COMPANY_PAYROLLS_PATHS.ROOT, end: true },
    { label: "Pay Slips", to: COMPANY_PAYROLLS_PATHS.PAY_SLIPS },
    { label: "Payment Files", to: COMPANY_PAYROLLS_PATHS.PAYMENT_FILES },
    { label: "Final Settlement", to: COMPANY_PAYROLLS_PATHS.FINAL_SETTLEMENTS },
    { label: "Disputes", to: COMPANY_PAYROLLS_PATHS.DISPUTES },
    { label: "Summary", to: COMPANY_PAYROLLS_PATHS.SUMMARY },
  ],

  // Company · My Apps
  [COMPANY_MY_APP_PATHS.ROOT]: [
    { label: "Apps", to: COMPANY_MY_APP_PATHS.ROOT, end: true },
    { label: "App Store", to: COMPANY_MY_APP_PATHS.APP_STORE },
  ],

  // My Space · My Apps
  [MS_MY_APP_PATHS.ROOT]: [
    { label: "Apps", to: MS_MY_APP_PATHS.ROOT, end: true },
    { label: "App Store", to: MS_MY_APP_PATHS.APP_STORE },
  ],

  // Company · Projects (single tab lives at the module root)
  [COMPANY_PROJECTS_PATHS.ROOT]: [
    { label: "Projects", to: COMPANY_PROJECTS_PATHS.ROOT, end: true },
  ],

  // Company · Events (single tab lives at the module root)
  [COMPANY_EVENT_PATHS.ROOT]: [
    { label: "List of Events", to: COMPANY_EVENT_PATHS.ROOT, end: true },
  ],

  // Company · Announcements (single tab lives at the module root — the
  // per-announcement detail page's Overview/Read-By-Users tabs are a separate,
  // single-entity view and are not part of this tab set)
  [COMPANY_ANNOUNCEMENTS_PATHS.ROOT]: [
    { label: "All Announcements", to: COMPANY_ANNOUNCEMENTS_PATHS.ROOT, end: true },
  ],

  // Company · Files
  [COMPANY_DATA_TRANSFER_PATHS.ROOT]: [
    { label: "Import", to: COMPANY_DATA_TRANSFER_PATHS.IMPORT },
    { label: "Export", to: COMPANY_DATA_TRANSFER_PATHS.EXPORT },
    { label: "History", to: COMPANY_DATA_TRANSFER_PATHS.HISTORY },
  ],
  [COMPANY_FILES_PATHS.ROOT]: [
    { label: "All Files", to: COMPANY_FILES_PATHS.ALL },
    { label: "Images", to: COMPANY_FILES_PATHS.IMAGES },
    { label: "Documents", to: COMPANY_FILES_PATHS.DOCUMENTS },
    { label: "Other", to: COMPANY_FILES_PATHS.OTHER },
  ],

  // My Space · Events (single tab lives at the module root)
  [MS_EVENT_PATHS.ROOT]: [
    { label: "List of Events", to: MS_EVENT_PATHS.ROOT, end: true },
  ],

  // Company · Employees
  [COMPANY_EMPLOYEES_PATHS.ROOT]: [
    { label: "Draft", to: COMPANY_EMPLOYEES_PATHS.DRAFT },
    { label: "Instant Onboarded", to: COMPANY_EMPLOYEES_PATHS.INSTANT },
    { label: "Active Employees", to: COMPANY_EMPLOYEES_PATHS.ACTIVE },
    { label: "All Employees", to: COMPANY_EMPLOYEES_PATHS.ALL },
    { label: "Offboarded", to: COMPANY_EMPLOYEES_PATHS.OFFBOARDED },
  ],

  // Company · Workflows
  [COMPANY_WORKFLOWS_PATHS.ROOT]: [
    { label: "Leave Management", to: COMPANY_WORKFLOWS_PATHS.LEAVE_MANAGEMENT },
    { label: "Asset Management", to: COMPANY_WORKFLOWS_PATHS.ASSET_MANAGEMENT },
    { label: "Overtime Requests", to: COMPANY_WORKFLOWS_PATHS.OVERTIME_REQUESTS },
  ],

  // Company · Subscription (outer tabs; inner vTab stays query-param)
  [COMPANY_SUBSCRIPTION_PATHS.ROOT]: [
    { label: "Dashboard", to: COMPANY_SUBSCRIPTION_PATHS.DASHBOARD },
    { label: "Management", to: COMPANY_SUBSCRIPTION_PATHS.MANAGEMENT },
    { label: "Activity", to: COMPANY_SUBSCRIPTION_PATHS.ACTIVITY },
    { label: "AI Token Usage", to: COMPANY_SUBSCRIPTION_PATHS.AI_TOKEN_USAGE },
    {
      label: "AI Token Usage Guidelines",
      to: COMPANY_SUBSCRIPTION_PATHS.AI_TOKEN_USAGE_GUIDELINES,
    },
  ],

  // My Space · Settings and Company · Settings are NOT here: main Settings is a
  // full page with its own grouped sidebar (config/settings-nav.config.ts), so
  // its main-sidebar entry is a plain link rather than an accordion.

  // My Space · Performance
  [MS_PERFORMANCE_PATHS.ROOT]: [
    { label: "My Reviews", to: MS_PERFORMANCE_PATHS.MY_REVIEWS },
    { label: "Review Requests", to: MS_PERFORMANCE_PATHS.REVIEW_REQUESTS },
  ],

  // Common (my-space shell) · Announcements
  [COMMON_PATHS.ANNOUNCEMENTS]: [
    { label: "All", to: COMMON_PATHS.ANNOUNCEMENTS_ALL },
    { label: "Unread", to: COMMON_PATHS.ANNOUNCEMENTS_UNREAD },
  ],

  // Common (my-space shell) · Notifications. NOTE: /company/notifications is a
  // SEPARATE route reusing the shared `Notifications` component (scope="company")
  // — untouched here; only this my-space-reachable route is converted.
  [COMMON_PATHS.NOTIFICATIONS]: [
    { label: "All", to: COMMON_PATHS.NOTIFICATIONS_ALL },
    { label: "Unread", to: COMMON_PATHS.NOTIFICATIONS_UNREAD },
  ],

  // My Space · Company Policies (single tab lives at the module root)
  [MS_COMPANY_POLICIES_PATHS.ROOT]: [
    { label: "Company Policies", to: MS_COMPANY_POLICIES_PATHS.ROOT, end: true },
  ],

  // Company · Performance (single tab lives at the module root — the
  // per-team detail page's Team Members/Configuration tabs are a separate,
  // already-tabbed sub-structure and are not part of this tab set)
  [COMPANY_PERFORMANCE_PATHS.ROOT]: [
    { label: "Teams", to: COMPANY_PERFORMANCE_PATHS.ROOT, end: true },
  ],

  // Company · Audit Logs (single tab lives at the module root — the category
  // breakdown is stat-filter tiles inside the page, not tabs)
  [COMPANY_AUDIT_LOGS_PATHS.ROOT]: [
    { label: "Audit Logs", to: COMPANY_AUDIT_LOGS_PATHS.ROOT, end: true },
  ],

  // Company · Cameras
  [COMPANY_CAMERA_PATHS.ROOT]: [
    { label: "Cameras", to: COMPANY_CAMERA_PATHS.ROOT, end: true },
  ],
};

/**
 * Tabs for a module, looked up by its sidebar link URL (`/${item.key}`), which
 * is the module root path. Returns `undefined` for modules without page tabs.
 */
export const getModuleNavTabs = (moduleUrl: string): NavTab[] | undefined =>
  MODULE_NAV_TABS[moduleUrl];

export type ModuleSettingsLink = {
  /** Row/tooltip label. Defaults to "Settings" at the call sites. */
  label: string;
  to: string;
  /**
   * The module this settings page belongs to. Both the page-tab CTA and the
   * sidebar's trailing settings row are gated on this section's `settings`
   * permission — a user without it must not see either affordance.
   */
  section: PLATFORM_SECTION_ENUM;
};

/**
 * A module's dedicated settings page — the destination of the "Settings" CTA in
 * the module's page-tab bar, and of the trailing settings row in the sidebar
 * accordion. Keyed by module ROOT path, exactly like `MODULE_NAV_TABS`.
 *
 * Deliberately NOT part of `MODULE_NAV_TABS`: settings must not become another
 * horizontal tab. Module-specific settings live here after being moved out of
 * the (now company-wide) Company Settings page.
 */
export const MODULE_SETTINGS_LINK: Record<string, ModuleSettingsLink> = {
  // Company · Teams
  [COMPANY_TEAMS_PATHS.ROOT]: {
    label: "Team Settings",
    to: COMPANY_TEAMS_PATHS.SETTINGS,
    section: PLATFORM_SECTION_ENUM.TEAMS,
  },

  // Company · Leave Management
  [COMPANY_LEAVES_MANAGEMENT_PATHS.ROOT]: {
    label: "Leave Settings",
    to: COMPANY_LEAVES_MANAGEMENT_PATHS.SETTINGS,
    section: PLATFORM_SECTION_ENUM.LEAVE_MANAGEMENT,
  },

  // Company · Time & Attendance (also owns the former Shift Settings tab and
  // the former standalone Overtime Policy page)
  [COMPANY_TIME_ATTENDANCE_PATHS.ROOT]: {
    label: "Time & Attendance Settings",
    to: COMPANY_TIME_ATTENDANCE_PATHS.SETTINGS,
    section: PLATFORM_SECTION_ENUM.TIME_ATTENDANCE,
  },

  // Company · Payroll (also owns the former Compensation Settings tab)
  [COMPANY_PAYROLLS_PATHS.ROOT]: {
    label: "Payroll Settings",
    to: COMPANY_PAYROLLS_PATHS.SETTINGS,
    section: PLATFORM_SECTION_ENUM.PAYROLL,
  },

  // Company · Assets
  [COMPANY_ASSETS_PATHS.ROOT]: {
    label: "Inventory & Assets Settings",
    to: COMPANY_ASSETS_PATHS.SETTINGS,
    section: PLATFORM_SECTION_ENUM.ASSETS,
  },

  // Company · Expenses
  [COMPANY_EXPENSES_PATHS.ROOT]: {
    label: "Expenses Settings",
    to: COMPANY_EXPENSES_PATHS.SETTINGS,
    section: PLATFORM_SECTION_ENUM.EXPENSES,
  },

  // Company · Sales & Commissions
  [COMPANY_SALES_PATHS.ROOT]: {
    label: "Commission Plans",
    to: COMPANY_SALES_PATHS.SETTINGS,
    section: PLATFORM_SECTION_ENUM.SALES_COMMISSIONS,
  },

  // Company · Biometric Devices
  [COMPANY_BIOMETRIC_DEVICES_PATHS.ROOT]: {
    label: "Agents",
    to: COMPANY_BIOMETRIC_DEVICES_PATHS.SETTINGS,
    section: PLATFORM_SECTION_ENUM.BIOMETRIC_DEVICES,
  },

  // Company · Documents
  [COMPANY_DOCUMENTS_PATHS.ROOT]: {
    label: "Documents Settings",
    to: COMPANY_DOCUMENTS_PATHS.SETTINGS,
    section: PLATFORM_SECTION_ENUM.DOCUMENTS,
  },

  // Company · Performance
  [COMPANY_PERFORMANCE_PATHS.ROOT]: {
    label: "Performance Settings",
    to: COMPANY_PERFORMANCE_PATHS.SETTINGS,
    section: PLATFORM_SECTION_ENUM.PERFORMANCE,
  },

  // Company · Onboarding
  [COMPANY_ONBOARDING_PATHS.ROOT]: {
    label: "Onboarding Settings",
    to: COMPANY_ONBOARDING_PATHS.SETTINGS,
    section: PLATFORM_SECTION_ENUM.ONBOARDING,
  },

  // Company · Recruitment
  [COMPANY_RECRUITMENT_PATHS.ROOT]: {
    label: "Recruitment Settings",
    to: COMPANY_RECRUITMENT_PATHS.SETTINGS,
    section: PLATFORM_SECTION_ENUM.RECRUITMENT,
  },

  // Company · Digital ID (was the "Digital ID Card" tab on Company Settings)
  [COMPANY_DIGITAL_ID_PATHS.ROOT]: {
    label: "Digital ID Settings",
    to: COMPANY_DIGITAL_ID_PATHS.SETTINGS,
    section: PLATFORM_SECTION_ENUM.DIGITAL_ID,
  },

  // Company · Audit Logs — retention window. Gated on canSettings, distinct
  // from canView, so a plain viewer can read the trail but not shorten
  // retention and erase evidence (see AUDIT_LOGS_PLAN.md §9).
  [COMPANY_AUDIT_LOGS_PATHS.ROOT]: {
    label: "Audit Log Settings",
    to: COMPANY_AUDIT_LOGS_PATHS.SETTINGS,
    section: PLATFORM_SECTION_ENUM.AUDIT_LOG,
  },

  // Company · Cameras — how long recordings are kept.
  [COMPANY_CAMERA_PATHS.ROOT]: {
    label: "Camera Settings",
    to: COMPANY_CAMERA_PATHS.SETTINGS,
    section: PLATFORM_SECTION_ENUM.CAMERA,
  },
};

/**
 * The module's settings destination, looked up by module root path. Returns
 * `undefined` for modules that have no dedicated settings page.
 */
export const getModuleSettingsLink = (
  moduleUrl: string,
): ModuleSettingsLink | undefined => MODULE_SETTINGS_LINK[moduleUrl];
